/**
 * Radio — single-choice form control. Always lives inside a `<RadioGroup>`
 * (or `<ChoiceGroup kind="radio">` in the future). A lone Radio has no
 * meaning — selecting it can never deselect, so there is no standalone
 * mode.
 *
 * Implementation note: visual is the Material Symbols glyph
 * `radio_button_unchecked` / `radio_button_checked` (sized per row),
 * NOT a CSS-built circle. Matches Figma's source and reuses our Icon
 * pipeline; no special shape-drawing.
 *
 * Sizing model — touch-target ROW + glyph:
 *
 *   ┌───────────── row ─────────────┐
 *   │   ⊙   Option label             │
 *   └────────────────────────────────┘
 *
 *   - `row` = the interactive height (s=24 / m=32 / l=48).
 *     Matches Toggle / Checkbox / Input / Button at the same step so all
 *     form-controls line up cleanly in a FormField.
 *   - `glyph` = the Material Symbols `font-size`, set to ≈1.2× the
 *     label's line-height so the artwork reads at the same scale as
 *     adjacent text (the artwork sits inside ~80% of the em-box).
 *
 * Default size: `m` (= Figma Small 32). Matches Input default so a
 * Radio next to an Input in the same FormField column lines up.
 *
 * States:
 *   - default: glyph `radio_button_unchecked` in `onSurfaceMuted`.
 *   - active:  glyph `radio_button_checked`   in `color.main`.
 *   - hover (inactive only): currentColor overlay halo + glyph shifts
 *     to `onSurface`. Active glyph shifts to `color.hover`.
 *     ACTIVE radio has NO halo overlay — selecting an already-
 *     selected radio offers no action, so no affordance is shown.
 *   - press (inactive only): overlay grows.
 *   - focus-visible: color-agnostic ring via `focusRing(theme)`.
 *   - disabled: uniform fade via `disabledStyle()`.
 *
 * Color — `primary` (default) / `good` / `bad` / `alert`. Cascades from
 * RadioGroup; per-Radio override possible. Use semantic tones to flag
 * positive / destructive / risky options in mixed lists.
 *
 * Width: defaults to `100%` so a Radio fills its parent column. Pass
 * `fullWidth={false}` to shrink to content (inline use). Radios are
 * always stacked column-wise — use SegmentedButton for inline rows.
 *
 * @format
 * @see Checkbox, Toggle for sibling form-controls in the same family.
 * @see ChoiceGroup for the multi-item compound that wraps Radio items under one `kind="radio"` API.
 */

import {createContext, forwardRef, useContext, useId, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text, type TypographyRole} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {focusRing, disabledStyle, stateOverlay} from 'ui/theme/state';

import {Icon} from '../Icon';

export const RADIO_SIZES = ['s', 'm', 'l'] as const;
export type RadioSize = (typeof RADIO_SIZES)[number];

export const RADIO_LABEL_POSITIONS = ['start', 'end'] as const;
export type RadioLabelPosition = (typeof RADIO_LABEL_POSITIONS)[number];

/** Semantic colour applied to the active glyph + focus ring + hover state. */
export const RADIO_COLORS = ['primary', 'good', 'bad', 'alert'] as const;
export type RadioColor = (typeof RADIO_COLORS)[number];

interface SizeGeometry {
  /** Touch-target row height. */
  row: number;
  /** Material Symbols glyph size — ≈1.2× the label's line-height. */
  glyph: number;
  /** Typography role used for the label. */
  labelRole: TypographyRole;
  /** Diameter (px) of the hover overlay halo. */
  hoverHalo: number;
  /** Diameter (px) of the press overlay (grow target). */
  pressHalo: number;
  /**
   * Symmetric vertical padding on the LabelText so a single label line
   * grows to the glyph's row height. Computed as `(row - labelLineHeight) / 2`.
   * For two-line rows (description present) this keeps the bottom
   * breathing room without recomputing.
   */
  labelVPad: number;
}

// Gap: row >> glyph so the slot's intrinsic padding creates the visual
// air to the label — no explicit gap needed.
const GEO: Record<RadioSize, SizeGeometry> = {
  s: {row: 24, glyph: 18, labelRole: 'label.s', hoverHalo: 20, pressHalo: 24, labelVPad: 5},
  m: {row: 32, glyph: 20, labelRole: 'label.m', hoverHalo: 24, pressHalo: 32, labelVPad: 8},
  l: {row: 48, glyph: 24, labelRole: 'label.l', hoverHalo: 32, pressHalo: 40, labelVPad: 14},
};

// ── Group context ─────────────────────────────────────────────────────

interface RadioGroupContextValue {
  name: string;
  value: string;
  onChange: (next: string) => void;
  size: RadioSize;
  disabled: boolean;
  labelPosition: RadioLabelPosition;
  color: RadioColor;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

// ── Styled primitives ──────────────────────────────────────────────────

const Root = styled('label', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: RadioSize;
  $labelPosition: RadioLabelPosition;
  $disabled: boolean;
  $fullWidth: boolean;
}>(({$size, $labelPosition, $disabled, $fullWidth}) => ({
  display: 'inline-flex',
  // Top alignment is unconditional. The glyph row is fixed; the LabelText
  // does its own symmetric padding (see below) so a 1-line label visually
  // centers next to the glyph, and a 2-line label extends DOWN without
  // shifting the glyph upward.
  alignItems: 'flex-start',
  flexDirection: $labelPosition === 'start' ? 'row-reverse' : 'row',
  minHeight: GEO[$size].row,
  width: $fullWidth ? '100%' : 'auto',
  cursor: 'pointer',
  userSelect: 'none',
  color: surfaceVar('onSurface'),
  ...text(GEO[$size].labelRole),
  ...($disabled && disabledStyle()),
}));

const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
  margin: 0,
});

/**
 * Glyph container. Holds the Material Symbols icon and paints the
 * hover/press halo via `::before`. Overlay tints in `currentColor`,
 * which we set per state so the halo auto-matches the glyph color.
 *
 * The halo only applies on `[data-active="false"]` — active radio
 * has no hover (clicking it does nothing).
 */
const GlyphSlot = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: RadioSize;
  $color: RadioColor;
  $active: boolean;
}>(({theme, $size, $color, $active}) => {
  const g = GEO[$size];
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: g.row,
    height: g.row,
    flexShrink: 0,
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.shorter,
    }),
    color: $active ? theme.palette[$color].main : surfaceVar('onSurfaceMuted'),
    'label:hover &': {
      color: $active ? theme.palette[$color].hover : surfaceVar('onSurface'),
    },
    ...stateOverlay({
      hoverSize: g.hoverHalo,
      pressSize: g.pressHalo,
      whenActive: false,
    }),
    ...focusRing(theme, {parentFocusVisible: true}),
    borderRadius: '50%',
  };
});

const LabelText = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: RadioSize}>(({theme, $size}) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: '1 1 auto',
  minWidth: 0,
  // Symmetric vertical padding so the label visually centers next to the
  // glyph in the 1-line case AND multi-line stacks keep breathing room
  // on top + bottom. Combined with `alignItems: flex-start` on Root, this
  // anchors the glyph and lets description extend the row downward.
  paddingTop: GEO[$size].labelVPad,
  paddingBottom: GEO[$size].labelVPad,
  gap: theme.spacing(1),
}));

const DescriptionText = styled(Box)(({}) => ({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  whiteSpace: 'normal',
}));

// ── Component ─────────────────────────────────────────────────────────

export interface RadioProps {
  // ── Required state ────────────────────────────────────────────────
  /** Value this Radio represents in the group. */
  value: string;
  /**
   * Label rendered alongside the glyph. REQUIRED — a bare radio glyph
   * has no meaning. Pass `ariaLabel` separately if the visible label
   * is a React node and you need a clean string for AT.
   */
  label: ReactNode;

  // ── Visual ────────────────────────────────────────────────────────
  /**
   * Per-item size override. Normally you set `size` on RadioGroup and
   * all children inherit. Pass this only when one item in a mixed-size
   * group needs to differ.
   */
  size?: RadioSize;
  /**
   * Per-item color override. Group-level `color` cascades; pass this
   * to paint a single Radio in a different semantic colour (e.g. one
   * `'bad'` option in an otherwise `'primary'` group).
   */
  color?: RadioColor;

  // ── Content ───────────────────────────────────────────────────────
  /**
   * Optional secondary muted line shown below `label`. Use for hints,
   * sub-titles, or short clarifications — anything that wouldn't fit
   * in the label without dominating it.
   */
  description?: ReactNode;
  /** Reverse the glyph/label order. Default `'end'` — label after glyph. */
  labelPosition?: RadioLabelPosition;

  // ── Behaviour ─────────────────────────────────────────────────────
  /**
   * Per-item disabled override. Group-level `disabled` cascades down;
   * a child cannot un-disable itself but can opt out individually.
   */
  disabled?: boolean;

  // ── Layout ────────────────────────────────────────────────────────
  /** Default `true` — fills the parent column. */
  fullWidth?: boolean;

  // ── Escape hatches ────────────────────────────────────────────────
  /** External `id` for the hidden input. Auto-generated when omitted. */
  id?: string;
  /** External `name` for the hidden input. The RadioGroup auto-generates one for browser arrow-key nav; pass this only when you need to integrate with a real HTML form. */
  name?: string;
  /**
   * Screen-reader-only label. Required when the visible `label` is a
   * React node (icon, badge, multi-element) and you need a clean string
   * for assistive tech.
   */
  ariaLabel?: string;
  /**
   * Pass-through className applied to the outer `<label>` element.
   * Use only for one-off positioning overrides.
   */
  className?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  {
    value,
    label,
    description,
    labelPosition,
    disabled: disabledProp,
    color: colorProp,
    size: sizeProp,
    fullWidth = true,
    id,
    name: nameProp,
    ariaLabel,
    className,
  },
  ref,
) {
  const group = useContext(RadioGroupContext);
  const autoId = useId();
  const inputId = id ?? autoId;
  if (!group) {
    // Guard against bare-Radio mistakes during development.
    throw new Error('Radio must be rendered inside a <RadioGroup>.');
  }

  const size = sizeProp ?? group.size;
  const disabled = group.disabled || (disabledProp ?? false);
  const color = colorProp ?? group.color;
  const labelPositionResolved = labelPosition ?? group.labelPosition;
  const name = nameProp ?? group.name;
  const checked = group.value === value;
  const glyphPx = GEO[size].glyph;

  const handleChange = () => {
    if (disabled || checked) return;
    group.onChange(value);
  };

  return (
    <Root
      htmlFor={inputId}
      $size={size}
      $labelPosition={labelPositionResolved}
      $disabled={disabled}
      $fullWidth={fullWidth}
      className={className}
    >
      <HiddenInput
        ref={ref}
        type='radio'
        id={inputId}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={handleChange}
      />
      <GlyphSlot
        $size={size}
        $color={color}
        $active={checked}
        data-active={checked ? 'true' : 'false'}
      >
        <Icon
          name={checked ? 'radio_button_checked' : 'radio_button_unchecked'}
          size={glyphPx}
          color='inherit'
        />
      </GlyphSlot>
      <LabelText $size={size}>
        <Box component='span'>{label}</Box>
        {description != null && <DescriptionText>{description}</DescriptionText>}
      </LabelText>
    </Root>
  );
});

// ── RadioGroup — controlled single-select compound ────────────────────

export interface RadioGroupProps {
  // ── Required state ────────────────────────────────────────────────
  /** Currently selected value. */
  value: string;
  /** Fires when the user picks a different option. */
  onChange: (next: string) => void;

  // ── Visual ────────────────────────────────────────────────────────
  /** Default `'m'`. Cascades to every child Radio. */
  size?: RadioSize;
  /**
   * Default `'primary'`. Cascades to every child Radio. Use `'good'`,
   * `'bad'`, or `'alert'` to colour the active glyph + focus ring +
   * hover state semantically.
   */
  color?: RadioColor;

  // ── Content ───────────────────────────────────────────────────────
  /** Default `'end'`. Cascades to every child Radio. */
  labelPosition?: RadioLabelPosition;

  // ── Behaviour ─────────────────────────────────────────────────────
  /** Disable the whole group; cascades to children. */
  disabled?: boolean;

  // ── Children + escape ────────────────────────────────────────────
  /** Required children — the `<Radio>` items. */
  children: ReactNode;
  className?: string;
}

const GroupWrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  // gap: 0 is the system rule — Radio rows stack flush. Not exposed
  // as a prop on purpose.
  gap: 0,
  width: '100%',
});

/**
 * RadioGroup — wraps `<Radio>` items in a shared context. Layout is
 * always column with no gap (use `<SegmentedButton mode="radio">` for
 * inline rows).
 */
export function RadioGroup({
  value,
  onChange,
  size = 'm',
  disabled = false,
  labelPosition = 'end',
  color = 'primary',
  children,
  className,
}: RadioGroupProps) {
  // name is internal — used by the browser to group radios for arrow-key
  // nav. Auto-generated; never exposed as a prop.
  const groupName = useId();
  const ctx: RadioGroupContextValue = {
    name: groupName,
    value,
    onChange,
    size,
    disabled,
    labelPosition,
    color,
  };
  return (
    <RadioGroupContext.Provider value={ctx}>
      <GroupWrap role='radiogroup' aria-disabled={disabled || undefined} className={className}>
        {children}
      </GroupWrap>
    </RadioGroupContext.Provider>
  );
}
